import { BookingBuilderContainer } from '@components/BookingBuilder/BookingBuilderContainer';
import { BookingBuilderProgress } from '@components/BookingBuilder/BookingBuilderProgress';
import { StuddedTireSeasonDialog } from '@components/BookingFlow/Common/StuddedTireSeasonDialog';
import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { captureException } from '@sentry/react';
import { useMutationState } from '@tanstack/react-query';
import { NoddiAsyncReturnType, URLKeys } from 'noddi-async';
import { NoddiAsyncError } from 'noddi-types';
import { customerRoutes } from 'noddi-util';
import { trackBookingCreated } from 'noddi-web';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrackProposalStep } from '../../../hooks/useTrackProposalStep';
import { useBookingProposalId } from '../../../stores/bookingProposalStore';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { checkoutRetryFromMutationVariables } from '../../../utils/stationaryCheckout';
import { useCreateBookingFromProposal } from '../hooks/useCreateBookingFromProposal';

export function ProposalBookingBuilder() {
  const navigate = useNavigate();
  const { currentBrand } = useCurrentBrand();
  const bookingProposalId = useBookingProposalId();
  useTrackProposalStep('building');

  const bookingMutation = useMutationState({
    filters: { mutationKey: [URLKeys.postCreateBookingIgnoringImpersonation, bookingProposalId] }
  });

  const failedMutation = bookingMutation.find((mutation) => mutation.status === 'error');
  const retry = checkoutRetryFromMutationVariables(failedMutation?.variables);
  const { createProposalBooking, getStuddedTireSeasonPrompt, isPending } = useCreateBookingFromProposal({
    paymentMethod: failedMutation ? retry.paymentMethod : undefined,
    userGroupPayerId: failedMutation ? retry.userGroupPayerId : undefined
  });

  // Keyed by attempt so a retry refused for the same reason prompts again.
  const [dismissedPromptAt, setDismissedPromptAt] = useState<number>();
  const studdedTireSeason =
    failedMutation && failedMutation.submittedAt !== dismissedPromptAt
      ? getStuddedTireSeasonPrompt(failedMutation.error, () => setDismissedPromptAt(failedMutation.submittedAt))
      : null;

  const isCreateBookingSuccessFull = bookingMutation.some((mutation) => mutation.status === 'success');
  // While the studded-tire dialog is open it owns the error surface; the builder must not render its error screen behind it.
  const isCreateBookingError = !studdedTireSeason && bookingMutation.some((mutation) => mutation.status === 'error');

  const data = bookingMutation.find((mutation) => mutation.status === 'success')?.data as NoddiAsyncReturnType<
    typeof URLKeys.postCreateBookingIgnoringImpersonation,
    typeof URLKeys.postCreateBookingIgnoringImpersonation
  >;

  useEffect(() => {
    if (isCreateBookingSuccessFull && data) {
      try {
        trackBookingCreated({
          isProposal: true,
          brand: currentBrand,
          bookingId: data.data?.id,
          order: data.data?.order
        });
      } catch (error) {
        captureException(error);
      }
      navigate(customerRoutes.confirmation.getPath({ slug: data.data.slug }), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy: reason not recorded; tracks the booking and redirects when the booking mutation succeeds
  }, [isCreateBookingSuccessFull]);

  return (
    <>
      {studdedTireSeason && <StuddedTireSeasonDialog {...studdedTireSeason} isPending={isPending} />}
      <BookingBuilderContainer
        isError={isCreateBookingError}
        isPaused={isCreateBookingError && !isPending}
        errorButton={(handleRestart) => (
          <div className='mt-4'>
            <ApiErrorMessageWithTrans
              error={bookingMutation.find((mutation) => mutation.status === 'error')?.error as NoddiAsyncError}
              isRetrying={isPending}
              onRetry={() => {
                handleRestart();
                // oxlint-disable-next-line typescript/no-floating-promises -- mutateAsync result unused; the retry failure is rendered from the mutation state above
                createProposalBooking();
              }}
            />
          </div>
        )}
      >
        {({ progress }) => <BookingBuilderProgress progress={progress} />}
      </BookingBuilderContainer>
    </>
  );
}
